/**
 * Image Generation Service
 * Handles API communication for generating visual diagrams of code insights
 */

import type { LensConstruct } from '../utils/lensParser'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface ImageGenerationRequest {
  conceptName: string
  code: string
  sourceLanguage: string
  targetLanguage: string
  concept: string
  paradigm: string
  surface: string
}

interface ImageGenerationResponse {
  success: boolean
  imageBase64?: string
  mimeType?: string
  textResponse?: string
  error?: string
  metadata?: {
    model: string
    responseTime: number
    conceptName?: string
    languages?: string
  }
}

/**
 * Generate an educational diagram for a code insight
 * @param construct - The lens construct to visualize
 * @param sourceLanguage - Source language (e.g., "C")
 * @param targetLanguage - Target language (e.g., "Rust")
 * @returns Base64-encoded image data URL
 */
export async function generateInsightImage(
  construct: LensConstruct,
  sourceLanguage: string,
  targetLanguage: string
): Promise<{ success: boolean; imageUrl?: string; error?: string }> {
  try {
    console.log(`🎨 Generating image for: ${construct.conceptName}`)

    const requestPayload: ImageGenerationRequest = {
      conceptName: construct.conceptName,
      code: construct.code,
      sourceLanguage,
      targetLanguage,
      concept: construct.concept,
      paradigm: construct.paradigm,
      surface: construct.surface
    }

    const response = await fetch(`${API_BASE_URL}/api/image/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestPayload)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`)
    }

    const data: ImageGenerationResponse = await response.json()

    if (!data.success || !data.imageBase64) {
      throw new Error(data.error || 'Image generation failed - no image returned')
    }

    // Convert base64 to data URL for direct display
    const imageUrl = `data:${data.mimeType || 'image/png'};base64,${data.imageBase64}`

    console.log(`✅ Image generated successfully (${data.metadata?.responseTime}ms)`)

    return {
      success: true,
      imageUrl
    }
  } catch (error) {
    console.error('❌ Image generation failed:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Image generation failed'
    }
  }
}

/**
 * Check if image generation service is available
 */
export async function checkImageGenerationHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/image/health`)
    if (!response.ok) return false

    const data = await response.json()
    return data.status === 'ok' && data.geminiConfigured === true
  } catch (error) {
    console.warn('Image generation health check failed:', error)
    return false
  }
}

/**
 * Image Generation Service
 */
export const imageGenerationService = {
  generateImage: generateInsightImage,
  checkHealth: checkImageGenerationHealth
}
